// Team & Seats — the owner's roster of employee accounts.
// Seats work the store; they never see legal, tax, payout, or store-wide
// financial data, which the UI states plainly so owners can trust it.

import React, { useState } from 'react';
import {
  UserPlus,
  Users,
  Mail,
  MoreVertical,
  ShieldCheck,
  Clock,
  Copy,
  Check,
  Loader2,
  Trash2,
  PauseCircle,
  PlayCircle,
  X,
  Lock,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { useSeats } from '../../../hooks/useSeats';
import { SEAT_ROLE_LABELS, type SeatRole, type SeatDocument } from '../../../types/business';

interface SeatsManagerProps {
  businessId: string | null;
}

function initials(name: string, email: string): string {
  const source = name?.trim() || email || '?';
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function formatWhen(value: any): string {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return 'Never';
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return date.toLocaleDateString();
}

export function SeatsManager({ businessId }: SeatsManagerProps) {
  const {
    seats,
    invites,
    joinRequests,
    joinCode,
    loading,
    inviteSeat,
    revokeInvite,
    updateSeatRole,
    setSeatStatus,
    removeSeat,
    resolveJoinRequest,
    ensureJoinCode,
  } = useSeats(businessId);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<SeatRole>('staff');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSending(true);
    try {
      await inviteSeat(inviteEmail.trim(), inviteName.trim(), inviteRole);
      notify('success', `Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('staff');
      setShowInvite(false);
    } catch (err: any) {
      notify('error', err?.message || 'Could not send that invitation');
    } finally {
      setSending(false);
    }
  };

  const runSeatAction = async (uid: string, action: () => Promise<unknown>, success: string) => {
    setBusyUid(uid);
    setOpenMenu(null);
    try {
      await action();
      notify('success', success);
    } catch (err: any) {
      notify('error', err?.message || 'That did not work');
    } finally {
      setBusyUid(null);
    }
  };

  const handleCopyCode = async () => {
    const code = joinCode || (await ensureJoinCode());
    await navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      await ensureJoinCode();
    } catch (err: any) {
      notify('error', err?.message || 'Could not create a join code');
    } finally {
      setGeneratingCode(false);
    }
  };

  return (
    <div className="space-y-4">
      {message && (
        <div
          className={cn(
            'rounded-xl px-4 py-3 text-sm',
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          )}
        >
          {message.text}
        </div>
      )}

      {/* What a seat can and cannot reach */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Lock className="h-4 w-4 text-primary" />
            </div>
            <div className="text-xs text-body leading-relaxed">
              <p className="text-sm font-semibold text-heading mb-1">Seats never see your private data</p>
              Employees with a seat can work orders, keep hours current, and track the sales they
              personally handled. They cannot open your EIN, W-9, bank or payout details, Stripe
              settings, store-wide revenue, or analytics.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Team Seats
              </h3>
              <p className="text-xs text-body mt-1">
                {seats.length === 0
                  ? 'No employees yet'
                  : `${seats.length} seat${seats.length === 1 ? '' : 's'}`}
              </p>
            </div>
            <Button size="sm" onClick={() => setShowInvite((prev) => !prev)}>
              {showInvite ? <X className="h-4 w-4 mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              {showInvite ? 'Cancel' : 'Add Seat'}
            </Button>
          </div>

          {showInvite && (
            <form onSubmit={handleInvite} className="space-y-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-heading">Full name</label>
                  <Input
                    type="text"
                    placeholder="Jordan Ellis"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-heading">
                    Work email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    required
                    placeholder="jordan@yourboutique.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-heading">Access level</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(Object.keys(SEAT_ROLE_LABELS) as SeatRole[]).map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setInviteRole(role)}
                      className={cn(
                        'text-left rounded-xl border p-3 transition-all',
                        inviteRole === role
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <span className="text-sm font-medium text-heading">
                        {SEAT_ROLE_LABELS[role].label}
                      </span>
                      <span className="block text-xs text-body mt-0.5">
                        {SEAT_ROLE_LABELS[role].description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={sending} className="w-full sm:w-auto">
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-1.5" />
                    Send invitation
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Requests waiting on the owner */}
      {joinRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-heading flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              Requests to join ({joinRequests.length})
            </h3>
            {joinRequests.map((request) => (
              <div
                key={request.uid}
                className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 border border-amber-100"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-heading truncate">
                    {request.displayName || request.email}
                  </p>
                  <p className="text-xs text-body truncate">{request.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyUid === request.uid}
                    onClick={() =>
                      runSeatAction(
                        request.uid,
                        () => resolveJoinRequest(request.uid, false),
                        'Request denied'
                      )
                    }
                  >
                    Deny
                  </Button>
                  <Button
                    size="sm"
                    disabled={busyUid === request.uid}
                    onClick={() =>
                      runSeatAction(
                        request.uid,
                        () => resolveJoinRequest(request.uid, true, 'staff'),
                        'Seat approved'
                      )
                    }
                  >
                    Approve as Staff
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Roster */}
      <Card>
        <CardContent className="p-4 space-y-2">
          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
            </div>
          ) : seats.length === 0 && invites.length === 0 ? (
            <div className="py-10 text-center">
              <Users className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-heading">No seats yet</p>
              <p className="text-xs text-body mt-1">
                Invite an employee so they can help run the store without access to your books.
              </p>
            </div>
          ) : (
            <>
              {seats.map((seat: SeatDocument) => (
                <div
                  key={seat.uid}
                  className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {initials(seat.displayName, seat.email)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-heading truncate">
                        {seat.displayName || seat.email}
                      </p>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[11px] font-semibold',
                          seat.seatRole === 'manager'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-gray-100 text-gray-600'
                        )}
                      >
                        {SEAT_ROLE_LABELS[seat.seatRole]?.label ?? seat.seatRole}
                      </span>
                      {seat.status === 'suspended' && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-600">
                          Suspended
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-body truncate">
                      {seat.email} · Active {formatWhen(seat.lastActiveAt)}
                    </p>
                  </div>

                  <div className="relative flex-shrink-0">
                    {busyUid === seat.uid ? (
                      <Loader2 className="h-4 w-4 text-primary animate-spin" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setOpenMenu(openMenu === seat.uid ? null : seat.uid)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4 text-gray-500" />
                      </button>
                    )}

                    {openMenu === seat.uid && (
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-gray-200 shadow-lg z-20 py-1">
                        <button
                          onClick={() =>
                            runSeatAction(
                              seat.uid,
                              () =>
                                updateSeatRole(
                                  seat.uid,
                                  seat.seatRole === 'manager' ? 'staff' : 'manager'
                                ),
                              'Access level updated'
                            )
                          }
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-heading hover:bg-gray-50 text-left"
                        >
                          <ShieldCheck className="h-4 w-4 text-gray-500" />
                          Make {seat.seatRole === 'manager' ? 'Staff' : 'Manager'}
                        </button>
                        <button
                          onClick={() =>
                            runSeatAction(
                              seat.uid,
                              () =>
                                setSeatStatus(
                                  seat.uid,
                                  seat.status === 'suspended' ? 'active' : 'suspended'
                                ),
                              seat.status === 'suspended' ? 'Seat reactivated' : 'Seat suspended'
                            )
                          }
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-heading hover:bg-gray-50 text-left"
                        >
                          {seat.status === 'suspended' ? (
                            <>
                              <PlayCircle className="h-4 w-4 text-gray-500" />
                              Reactivate access
                            </>
                          ) : (
                            <>
                              <PauseCircle className="h-4 w-4 text-gray-500" />
                              Suspend access
                            </>
                          )}
                        </button>
                        <button
                          onClick={() =>
                            runSeatAction(
                              seat.uid,
                              () => removeSeat(seat.uid),
                              'Seat removed'
                            )
                          }
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove from store
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {invites.map((invite) => (
                <div
                  key={invite.token}
                  className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-heading truncate">
                        {invite.displayName || invite.email}
                      </p>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">
                        Invited
                      </span>
                    </div>
                    <p className="text-xs text-body truncate">
                      {invite.email} · {SEAT_ROLE_LABELS[invite.seatRole]?.label ?? invite.seatRole}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyUid === invite.token}
                    onClick={() =>
                      runSeatAction(
                        invite.token,
                        () => revokeInvite(invite.token),
                        'Invitation revoked'
                      )
                    }
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Join code for the request-and-approve path */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-heading mb-1">Store join code</h3>
          <p className="text-xs text-body mb-3">
            An employee who already has a FASH account can enter this code to request a seat.
            You approve the request before they get any access.
          </p>

          {joinCode ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 font-mono text-lg tracking-[0.25em] text-heading">
                {joinCode}
              </code>
              <Button variant="outline" size="sm" onClick={handleCopyCode}>
                {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={handleGenerateCode} disabled={generatingCode}>
              {generatingCode ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              Create join code
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
